import React, { useEffect, useState } from 'react'
import DefaultButton from '../../components/Button/Button'
import { useFormik } from 'formik'
import { residentSchema } from '../../validations/residentForm'
import { IAddResident } from '../../interfaces/resident'
import ResidentService from '../../api/resident-service'
import { showToast } from '../../utils/toast'
import FormCheckBox from '../../components/CheckBox'
import Dropdown from '../../components/Dropdown'
import ResidentTypeService from '../../api/resident-type-service'
import LandlordDataSerivce from '../../api/landlord-service'
import { Landlord, PropertyDTO } from '../../interfaces/landlord'
import FormInputRow from '../../components/Input/FormInputRow'
import DateInput from '../../components/Input/DateInput'
import { getLocal } from '../../utils/local'

import { useNavigate } from 'react-router-dom'
import WarningIcon from '../../assets/icons/warningImage.png'
import AddResidentConfirmationModal from '../../components/Resident/AddResidentConfirmationModal'


interface AddResidentProps {
  handleViewSelect: (view: string, queries?: Record<string, string>) => void
  isEdit?: boolean
}

const AddResident = ({ handleViewSelect, isEdit }: AddResidentProps) => {
  const getEstate = getLocal('estate')
  const { id: estateId } = getEstate || {}
  const [loading, setLoading] = React.useState(false)
  const [loadingResidentType, setLoadingResidentType] = useState(true)
  const [propertyLoading, setPropertyLoading] = useState(false)
  const [properties, setProperties] = React.useState<{ value: string; label: string }[]>(
    []
  )
  const [landlordProperties, setLandlordProperties] = React.useState<PropertyDTO[]>([])

  const [initialValues] = React.useState<IAddResident>({
    email: '',
    isOwner: true,
    residentType: '',
    isResident: true,
    dateOfOccupancy: '',
    address: '',
    landlord: '',
    property: '',
    capacity: 0,
  })

  const [residentType, setResidentType] = React.useState<
    { value: string; label: string }[]
  >([])

  const [landlords, setLandlords] = useState<Landlord[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const org = getLocal('estate')

  const isFullyVerified = Boolean(
    (org?.type === 'individual'
      ? org?.isKycVerified
      : org?.isKybVerified) &&
    org?.bankAccountVerified
  )

  const navigate = useNavigate()

  const submitForm = async (data: IAddResident) => {
    if (loading) return

    setLoading(true)
    try {
      await ResidentService.addResident(data)
      showToast({
        title: 'You have successfully added a Resident',
        description: 'An email has been sent to your new resident.',
        id: 'add-resident-success',
        status: 'success',
      })
      setIsModalOpen(true)
    } catch (err: any) {
      showToast({
        title: 'An error occured creating reisdent',
        description: err.message,
        id: 'add-resident-error',
        status: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema: residentSchema,
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: false,
    enableReinitialize: true,
    onSubmit: (values) => {
      const { isResident, landlord, residentType, property, ...rest } = values
      const data: IAddResident = {
        ...rest,
        isResident,
        residentType,
        landlord: isResident && landlord ? landlord : undefined,
        property: isResident && landlord ? property : undefined,
      }

      if (isResident && landlord) {
        // capacity is owner-only
        delete (data as any).capacity
      }

      submitForm(data)
    },
  })

  useEffect(() => {
    if (!estateId) return

    if (formik.values.isOwner) {
      setLoadingResidentType(true)
      ResidentTypeService.getResidentType({
        estateId,
        page: 1,
        limit: 10,
        all: false,
      })
        .then((data) => {
          setResidentType(
            data.results.map((record) => ({
              value: record.id,
              label: record.title,
            }))
          )
        })
        .catch((err) => {
          showToast({
            title: 'Error fetching resident type',
            description: err.message,
            status: 'error',
            id: 'get-resident-ype-error',
          })
        })
        .finally(() => setLoadingResidentType(false))
    } else {
      setPropertyLoading(true)
      LandlordDataSerivce.getLandlordDataList({ all: true })
        .then(({ results }) => {
          setLandlords(
            results.map((record) => ({
              value: record.id,
              label:
                `${record.resident.firstName ?? ''} ${record.resident.lastName ?? ''}`.trim() ||
                (record.resident.name ?? 'Name Unavailable'),
            }))
          )
        })
        .catch((err) => {
          showToast({
            title: 'Error fetching landlords',
            description: err.message,
            status: 'error',
            id: 'get-resident-ype-error',
          })
        })
        .finally(() => setPropertyLoading(false))
    }
  }, [estateId, formik.values.isOwner])

  React.useEffect(() => {
    if (formik.values.landlord) {
      setPropertyLoading(true)
      LandlordDataSerivce.getOwnedProperties(formik.values.landlord)
        .then((data) => {
          setProperties(
            data.results.map((property) => ({
              value: property.id,
              label: property.address,
            }))
          )

          setLandlordProperties(data.results)
        })
        .catch((err) => {
          showToast({
            title: 'Error fetching properties',
            description: err.message,
            status: 'error',
            id: 'get-properties-error',
          })
        })
        .finally(() => {
          setPropertyLoading(false)
          formik.setFieldValue('property', '')
          formik.setFieldValue('address', '')
          setResidentType([])
        })
    }
  }, [formik.values.landlord])

  React.useEffect(() => {
    if (formik.values.property) {
      const selectedProperty = landlordProperties.find(
        (property) => property.id === formik.values.property
      )

      if (selectedProperty) {
        formik.setFieldValue('address', selectedProperty.address)
        const capacityList = selectedProperty.capacity

        const residentTypeList = []
        for (const capacity of capacityList) {
          if (
            capacity.capacity > capacity.takenCapacity &&
            capacity.residentType.isDeleted !== true
          ) {
            residentTypeList.push({
              value: capacity.residentType.id,
              label: capacity.residentType.title,
            })
          }
        }

        formik.setFieldValue('residentType', '')
        setResidentType(residentTypeList)
      }
    }
  }, [formik.values.property])

  React.useEffect(() => {
    if (!formik.values.isOwner) {
      formik.setFieldValue('residentType', '')
      formik.setFieldValue('landlord', '')
      formik.setFieldValue('property', '')
      formik.setFieldValue('address', '')
      formik.setFieldValue('capacity', 0)
      setResidentType([])
    } else {
      formik.setFieldValue('landlord', '')
      formik.setFieldValue('property', '')
    }
  }, [formik.values.isOwner])

  // always clear residentType when not a resident
  React.useEffect(() => {
    if (!formik.values.isResident) {
      formik.setFieldValue('residentType', '')
      formik.setFieldValue('landlord', '')
      formik.setFieldValue('property', '')
      formik.setFieldValue('address', '')
    }
  }, [formik.values.isResident])



  // single submit path with correct payload shaping
  const handleSubmit = () => {
    if (loading || !formik.dirty) return

    const { isResident, landlord, property, residentType, ...rest } = formik.values

    const data: IAddResident = {
      ...rest,
      isResident,
      residentType,
      landlord: isResident && landlord ? landlord : undefined,
      property: isResident && landlord ? property : undefined,
    }

    if (isResident && landlord) {
      delete (data as any).capacity
    }

    submitForm(data)
  }

  const handleSelectResidentType = (value: string) => {
    formik.setFieldValue('residentType', value, true)
    formik.setFieldTouched('residentType', true, false)
  }

  const handleSelectProperty = (value: string) => {
    formik.setFieldValue('property', value, true)
    formik.setFieldTouched('property', true, false)
  }

  const handleSelectLandlord = (value: string) => {
    formik.setFieldValue('landlord', value, true)
    formik.setFieldTouched('landlord', true, false)
  }

  const handleAddBankAccount = () => {
    if (!org?.id) return
    navigate(`/estates/${org.id}/edit?active=4`)
  }
  return (
    <div className="mt-12 grid gap-y-10">
      <>
        {
          !isFullyVerified && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 px-4 py-4 rounded-md">
              <div className="flex items-start gap-3">
                <img
                  src={WarningIcon}
                  alt="warning"
                  className="w-5 h-5 mt-1 flex-shrink-0"
                />
                <span className="text-sm md:text-base text-gray-600 font-medium leading-relaxed">
                  This community has not completed verification.
                  Residents will not be able to pay levies.
                </span>
              </div>
              <button
                onClick={handleAddBankAccount}
                className="w-full md:w-auto text-sm md:text-base font-medium text-primary hover:underline text-left md:text-right"
              >
                Add bank account
              </button>
            </div>
          )
        }
        <div className="mt-5 flex flex-col space-y-10 text-gray-400 font-medium">
          <p className="text-lg font-bold leading-7 text-black">Resident details</p>
          <div className="space-y-2.5">
            <p className="font-medium leading-6 text-[#6C757D]">Select an Option</p>
            <div className="flex flex-wrap gap-4">
              <FormCheckBox
                setChecked={(checked) => formik.setFieldValue('isOwner', checked, true)}
                checked={formik.values.isOwner}
                label="Property owner"
                error={formik.errors.isOwner}
              />
              <FormCheckBox
                setChecked={(checked) => formik.setFieldValue('isResident', checked, true)}
                checked={formik.values.isResident}
                label="Resident"
                error={formik.errors.isResident}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {!formik.values.isOwner && (
              <div className="space-y-2.5 w-full">
                <p className="text-black font-medium text-base leading-7">Landlord</p>
                <Dropdown
                  options={landlords}
                  onSelect={handleSelectLandlord}
                  defaultLabel="Select Landlord"
                  defaultValue={formik.values.landlord}
                  disabled={!formik.values.isResident}
                />
              </div>
            )}
            {!formik.values.isOwner && (
              <div className="space-y-2.5 w-full">
                <p className="text-gray-400 font-medium text-base leading-7">Property</p>
                <Dropdown
                  options={properties}
                  onSelect={handleSelectProperty}
                  defaultLabel="Select property"
                  loading={propertyLoading}
                  defaultValue={formik.values.property}
                  disabled={!formik.values.landlord || !formik.values.isResident}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormInputRow
              label="Email Address"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              placeholder="Enter your email"
              error={formik.errors.email}
              className="w-full"
            />
            <FormInputRow
              label="Address"
              name="address"
              value={formik.values.address}
              onChange={formik.handleChange}
              placeholder="5 Safelto Street, Safelto Estate"
              error={!!formik.values.property ? '' : formik.errors.address}
              className="w-full"
              disabled={!!formik.values.property}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <DateInput
              value={formik.values.dateOfOccupancy}
              onChange={formik.handleChange}
              name="dateOfOccupancy"
              label="Date of Occupancy"
              placeholder="19/07/2023"
              error={formik.errors.dateOfOccupancy}
              className="w-full"
            />

            <div className="space-y-2.5 w-full">
              <p className="text-gray-400 font-medium text-base leading-7">
                Resident Type
              </p>
              <Dropdown
                options={residentType}
                onSelect={handleSelectResidentType}
                defaultLabel="Select resident type"
                loading={loadingResidentType}
                defaultValue={formik.values.residentType}
              // disabled={!formik.values.isResident}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {formik.values.isOwner && (
              <FormInputRow
                label="Capacity"
                name="capacity"
                value={formik.values.capacity!}
                onChange={formik.handleChange}
                placeholder="Enter property capacity"
                error={formik.errors.capacity}
                className="w-full"
                type="number"
              />
            )}
          </div>
        </div>

        <div className="flex justify-end sm:mt-20 lg:mt-10 py-3 lg:px-6 gap-5">
          <DefaultButton
            text={'Cancel'}
            buttonType='secondary'
            onClick={() => handleViewSelect('view')}
          />
          <DefaultButton
            text={'Add Resident'}
            className="sm:w-54 grow sm:grow-0 hover:grow-[9] hover:sm:grow-0 "
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Creating..."
            disabled={loading || !(formik.dirty && formik.isValid)}
          />
        </div>
      </>
      <AddResidentConfirmationModal
        isOpen={isModalOpen}
        email={formik.values.email}
        onCancel={() => {
          setIsModalOpen(false)
          handleViewSelect('view')
        }}
        onConfirm={() => {
          formik.resetForm()
          setIsModalOpen(false)
          handleViewSelect('add')
        }}
      />
    </div>
  )
}

export default AddResident
